import {useMemo} from 'react'
import {useInvalidateOnAliveUpdate, type AliveUpdateData} from '@github-ui/use-invalidate-on-alive-update'
import {graphql} from 'relay-runtime'

import {nestedSubIssuesQueryKey} from '../components/fetch-nested-sub-issues'

// This subscription is just temporary until it's removed from the backend. It's not really used anymore, but we need to first ship a backend PR to remove the preloading of it.
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries, @typescript-eslint/no-unused-expressions
graphql`
  subscription subIssueSubscription($issueId: ID!, $fetchSubIssues: Boolean!) {
    issueUpdated(id: $issueId) {
      issueBodyUpdated {
        id @include(if: $fetchSubIssues)
      }
    }
  }
`

type IssueUpdatedAliveData = AliveUpdateData & {
  issue_state_updated?: true
  issue_title_updated?: true
  issue_body_updated?: true
  issue_type_updated?: true
  issue_transfer_state_updated?: true
  issue_metadata_updated?: true
  issue_timeline_updated?: true
  issue_reaction_updated?: true
  parent_issue_updated?: true
  sub_issues_updated?: true
  sub_issues_summary_updated?: true
  issue_dependencies_summary_updated?: true
  issue_fields_updated?: true
  update_intent_changed?: true
  deleted_comment_id?: string
  comment_updated_id?: string
  reacted_comment_id?: string
  pinned_comment_updated_id?: string
}

const ISSUE_UPDATE_METADATA_FIELDS = [
  'issue_state_updated',
  'issue_title_updated',
  'issue_body_updated',
  'issue_type_updated',
  'issue_transfer_state_updated',
  'issue_metadata_updated',
  'issue_timeline_updated',
  'issue_reaction_updated',
  'parent_issue_updated',
  'sub_issues_updated',
  'sub_issues_summary_updated',
  'issue_dependencies_summary_updated',
  'issue_fields_updated',
  'update_intent_changed',
  'deleted_comment_id',
  'comment_updated_id',
  'reacted_comment_id',
  'pinned_comment_updated_id',
] as const satisfies ReadonlyArray<keyof IssueUpdatedAliveData>

const SUB_ISSUE_ROW_UPDATE_FIELDS = [
  'issue_state_updated',
  'issue_title_updated',
  'issue_type_updated',
  'issue_transfer_state_updated',
  'issue_metadata_updated',
  'parent_issue_updated',
  'sub_issues_updated',
  'sub_issues_summary_updated',
  'issue_dependencies_summary_updated',
] as const satisfies ReadonlyArray<keyof IssueUpdatedAliveData>

const isSubIssueRowUpdate = (data: IssueUpdatedAliveData) => {
  const hasIssueUpdateMetadata = ISSUE_UPDATE_METADATA_FIELDS.some(field => data[field] != null)
  return !hasIssueUpdateMetadata || SUB_ISSUE_ROW_UPDATE_FIELDS.some(field => data[field] != null)
}

const isSubIssuesCollectionUpdate = (data: IssueUpdatedAliveData) => {
  const hasIssueUpdateMetadata = ISSUE_UPDATE_METADATA_FIELDS.some(field => data[field] != null)
  return !hasIssueUpdateMetadata || data.sub_issues_updated === true || data.sub_issues_summary_updated === true
}

export function useSubIssueSubscription(
  channel: string | null | undefined,
  parentOwner: string,
  parentRepo: string,
  parentNumber: number,
) {
  const parentQueryKey = useMemo(
    () => nestedSubIssuesQueryKey(parentOwner, parentRepo, parentNumber),
    [parentNumber, parentOwner, parentRepo],
  )
  useInvalidateOnAliveUpdate(channel, parentQueryKey, {filter: isSubIssueRowUpdate})
}

export function useNestedSubIssuesSubscription(
  channel: string | null | undefined,
  owner: string,
  repo: string,
  number: number,
) {
  const queryKey = useMemo(() => nestedSubIssuesQueryKey(owner, repo, number), [number, owner, repo])
  useInvalidateOnAliveUpdate(channel, queryKey, {filter: isSubIssuesCollectionUpdate})
}
